import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Badge, Breadcrumbs, Card, Empty, ErrorNotice, Loading, Notice, Pager, fmt, fmtDate, useToast,
} from '../components/ui.jsx';

const LIMIT = 25;

const STATUS_KIND = {
  completed: 'approved', failed: 'rejected', running: 'pending',
  queued: 'pending', mapping: 'pending', uploaded: 'pending', cancelled: 'superseded',
};

export default function ImportsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);

  const query = useQuery({
    queryKey: ['imports', offset],
    queryFn: () => api.listBatches({ limit: LIMIT, offset }),
    // Keep the list fresh while any batch is still running.
    refetchInterval: (q) => ((q.state.data?.data || []).some((b) => ['running', 'queued'].includes(b.status)) ? 3000 : false),
  });

  const rematch = useMutation({
    mutationFn: ({ id, onlyUnmatched }) => api.rematchBatch(id, onlyUnmatched),
    onSuccess: () => {
      toast.success('Match run started');
      queryClient.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = query.data?.data || [];
  const total = query.data?.meta?.total || 0;

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Import history' }]} />
      <div className="page-head">
        <div>
          <h1>Import history</h1>
          <div className="page-sub">
            Every master file ever loaded, with its checksum, the column mapping used, and the outcome of its
            match run. This is the record an auditor asks for.
          </div>
        </div>
        <div className="spacer" />
        {can('reviewer') && <Link className="btn btn-primary" to="/upload">Upload master</Link>}
      </div>

      <Notice tone="info" title="When to re-run a match">
        After adding new vehicles to the GIIB master, re-run the affected insurer's most recent batch. Rows a
        person has already approved are never overwritten - if the engine now disagrees with an approved link,
        it raises a new pending row instead, and that appears in the review queue.
      </Notice>

      <Card bodyClass="tight">
        {query.isLoading ? <div style={{ padding: 16 }}><Loading rows={6} /></div>
          : query.error ? <div style={{ padding: 16 }}><ErrorNotice error={query.error} /></div>
            : rows.length === 0 ? <Empty title="No imports yet"><Link to="/upload">Upload the first master</Link></Empty>
              : (
                <>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Batch</th>
                          <th>Target</th>
                          <th>Status</th>
                          <th className="num">Rows</th>
                          <th className="num">New</th>
                          <th className="num">Auto</th>
                          <th className="num">Review</th>
                          <th className="num">No match</th>
                          <th>Finished</th>
                          <th>By</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((b) => (
                          <tr key={b.id}>
                            <td>
                              <div className="cell-main">{b.filename}</div>
                              <div className="cell-sub">
                                #{b.id}
                                {b.checksum && <span className="prov"> · sha256 {b.checksum.slice(0, 10)}</span>}
                              </div>
                            </td>
                            <td>
                              {b.target === 'giib_master' ? 'GIIB master' : b.insurerCode || '-'}
                              {b.columnMap && (
                                <div className="prov">
                                  code column: {b.columnMap.icCode || 'n/a'}
                                </div>
                              )}
                            </td>
                            <td>
                              <Badge kind={STATUS_KIND[b.status] || 'neutral'}>{b.status}</Badge>
                              {b.status === 'running' && (
                                <div className="prov">{fmt(b.processedRows)} / {fmt(b.totalRows)}</div>
                              )}
                              {b.errorMessage && <div className="prov" style={{ maxWidth: 220 }}>{b.errorMessage}</div>}
                            </td>
                            <td className="num">{fmt(b.totalRows)}</td>
                            <td className="num">{fmt(b.newRows)}</td>
                            <td className="num">{fmt(b.autoMatched)}</td>
                            <td className="num">{fmt(b.needsReview)}</td>
                            <td className="num">{fmt(b.unmatched)}</td>
                            <td>{fmtDate(b.finishedAt)}</td>
                            <td>{b.createdBy || '-'}</td>
                            <td>
                              {can('reviewer') && b.target === 'ic_master' && (
                                <div className="btn-row">
                                  <button
                                    className="btn btn-sm"
                                    disabled={rematch.isPending || b.status === 'running'}
                                    onClick={() => rematch.mutate({ id: b.id, onlyUnmatched: false })}
                                  >
                                    Re-run
                                  </button>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    disabled={rematch.isPending || b.status === 'running'}
                                    onClick={() => rematch.mutate({ id: b.id, onlyUnmatched: true })}
                                    title="Only score rows that are still undecided"
                                  >
                                    Unmatched only
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
                </>
              )}
      </Card>
    </div>
  );
}
